import { captureMessage } from './sentry';

export const DASHBOARD_ROUTE_LOADING_ALERT_SCOPE = 'dashboard_route_loading';
export const DASHBOARD_ROUTE_LOADING_MESSAGE = 'Dashboard route loading issue';

type DashboardRouteLoadingFailure =
    | 'lazy_route_timeout'
    | 'project_detail_not_found'
    | 'project_detail_load_exception';

type DashboardRouteLoadingDetails = {
    routeLabel?: string;
    projectId?: string;
    timeoutMs?: number;
    errorMessage?: string;
};

const getBrowserState = () => {
    if (typeof window === 'undefined') return {};
    return {
        pathname: window.location.pathname,
        search_present: Boolean(window.location.search),
        visibility_state: typeof document !== 'undefined' ? document.visibilityState : undefined,
        online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    };
};

export const captureDashboardRouteLoadingIssue = (
    failureType: DashboardRouteLoadingFailure,
    details: DashboardRouteLoadingDetails = {},
) => {
    const routeLabel = details.routeLabel || 'unknown';

    captureMessage(DASHBOARD_ROUTE_LOADING_MESSAGE, 'warning', {
        tags: {
            alert_scope: DASHBOARD_ROUTE_LOADING_ALERT_SCOPE,
            failure_type: failureType,
            route_label: routeLabel,
            surface: 'dashboard',
        },
        extra: {
            ...getBrowserState(),
            ...details,
        },
        fingerprint: ['dashboard-route-loading-issue', failureType, routeLabel],
    });
};

/**
 * Fired when the post-login success screen is still mounted several seconds
 * after a successful login — i.e. the in-app navigate('/dashboard') hand-off
 * never completed (typically a stalled lazy Dashboard chunk under React
 * Router v7's default startTransition). The watchdog that reports this also
 * forces a full-page recovery navigation, so this is the only signal we get
 * for the otherwise-silent "stuck on the checkmark" failure.
 */
export const captureLoginHandoffStall = (
    details: { role?: string; timeoutMs?: number } = {},
) => {
    captureMessage('Login → dashboard hand-off stalled', 'warning', {
        tags: {
            alert_scope: DASHBOARD_ROUTE_LOADING_ALERT_SCOPE,
            failure_type: 'login_handoff_stall',
            surface: 'auth',
        },
        extra: {
            ...getBrowserState(),
            ...details,
        },
        fingerprint: ['login-handoff-stall'],
    });
};
